/**
 * Smart caching utility with timestamp-based expiration
 */

import { logger } from './logger';

interface CacheEntry<T> {
  data: T
  timestamp: number
}

/**
 * Get cached data if it exists and is not stale
 * @param key Cache key
 * @param maxAge Maximum age in milliseconds (default: 30 seconds)
 * @returns Cached data or null if not found/stale
 */
export function getCachedData<T>(key: string, maxAge: number = 30000): T | null {
  try {
    const cached = sessionStorage.getItem(key)
    if (!cached) return null
    
    const entry: CacheEntry<T> = JSON.parse(cached)
    const age = Date.now() - entry.timestamp
    
    // Return null if cache is too old
    if (age > maxAge) return null
    
    return entry.data
  } catch {
    return null
  }
}

/**
 * Set cached data with current timestamp
 * @param key Cache key
 * @param data Data to cache
 */
export function setCachedData<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now()
    }
    sessionStorage.setItem(key, JSON.stringify(entry))
  } catch (error) {
    logger.warn('Failed to cache data:', error)
  }
}

/**
 * Invalidate cache for a specific key
 * @param key Cache key to invalidate
 */
export function invalidateCache(key: string): void {
  try {
    sessionStorage.removeItem(key)
  } catch (error) {
    logger.warn('Failed to invalidate cache:', error)
  }
}

/**
 * Clear all cache
 */
export function clearAllCache(): void {
  try {
    sessionStorage.clear()
  } catch (error) {
    logger.warn('Failed to clear cache:', error)
  }
}

